import { useMemo } from "react"
import { z } from "zod"

import {
  CustomFieldConstraint,
  TraceabilityResponses,
} from "@tfg/types/endpoints/traceability"

import { CustomFieldValue } from "~/pages/SupplierPortal/TraceabilityRequests/components/CustomFieldInput"
import { useInformationRequestContext } from "~/pages/SupplierPortal/TraceabilityRequests/store/InformationRequestContext"
import { OperationItem } from "~/pages/SupplierPortal/TraceabilityRequests/utils"

const stringArraySchema = z.array(z.string())

export function useCustomFieldInitialValue(
  cfd: TraceabilityResponses.PublicCustomFieldDef,
  setError: (error: string | undefined) => void,
  operationItem: OperationItem
): CustomFieldValue {
  const { customFieldValues } = useInformationRequestContext()
  return useMemo(() => {
    const customFieldValue = customFieldValues
      .filter((cfv) => cfv.operationId === operationItem.operation?.id)
      .find((x) => x.customFieldDefinitionId === cfd.id)

    if (!customFieldValue) {
      return {
        id: "",
        value: "",
        document: null,
        selectedFile: null,
        customFieldDefinitionId: cfd.id,
        operationId: operationItem.operation?.id ?? 0,
      }
    }

    const rawInitialValue: string = customFieldValue.value

    if (cfd.constraint === CustomFieldConstraint.MULTI_SELECT) {
      let parsedValue: string[]
      try {
        if (rawInitialValue && rawInitialValue !== "") {
          parsedValue = stringArraySchema.parse(JSON.parse(rawInitialValue))
        } else {
          parsedValue = []
        }
      } catch {
        parsedValue = []
        setError("Invalid value in input, please contact support")
      }
      return {
        id: customFieldValue.id,
        value: parsedValue,
        document: customFieldValue.document,
        selectedFile: null,
        customFieldDefinitionId: cfd.id,
        operationId: operationItem.operation?.id ?? 0,
      }
    }

    return {
      id: customFieldValue.id,
      value: rawInitialValue,
      document: customFieldValue.document,
      selectedFile: null,
      customFieldDefinitionId: cfd.id,
      operationId: operationItem.operation?.id ?? 0,
    }
  }, [cfd.id, cfd.constraint, setError, customFieldValues, operationItem])
}
